import getSymbolFromCurrency from "currency-symbol-map";
import moment from "moment";
import copy from "clipboard-copy";
import PropTypes from "prop-types";
import submit from "submit-form";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getDivisor } from "../../../../utils/stripe-helpers";
import SvgIcon from "../../../../parts/SvgIcon";

const Coupon = ({ coupon }) => {
  const { t } = useTranslation("coaching-portal");
  const {
    id,
    amount_off,
    currency,
    duration,
    duration_in_months,
    percent_off,
    name,
    redeem_by,
    max_redemptions,
    metadata,
    times_redeemed,
    valid,
  } = coupon;
  const [showTooltip, setShowTooltip] = useState(false);
  const symbol = getSymbolFromCurrency(currency ?? "usd");
  const divisor = getDivisor(currency ?? "usd");
  const amount = (amount_off ?? 0) / divisor;
  let durationString = duration;
  if (duration === "repeating") {
    durationString = `${duration_in_months} months`;
  }
  let colors = ["#5bc500", "#42ac00"];
  if (metadata?.colors) {
    colors = JSON.parse(metadata.colors);
  }
  if (!valid) {
    colors = ["gray", "darkgray"];
  }

  const copyCode = async () => {
    await copy(id);
    setShowTooltip(true);
    setTimeout(() => {
      setShowTooltip(false);
    }, 1250);
  };

  const deleteCoupon = () => {
    if (confirm(t("planManager.coupon.deleteConfirm"))) {
      submit(`/actions/plan-manager/delete-coupon/${id}`);
    }
  };

  return (
    <article
      className="coupon"
      style={{
        background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`,
      }}
    >
      <div className="coupon-header">
        <h1 className="price">
          {percent_off ? `${percent_off}%` : `${symbol}${amount}`}{" "}
          {t("planManager.coupon.off")}
        </h1>
        <p className="title">{name || id}</p>
        <p className="validity">{durationString}</p>
      </div>
      <div
        className="coupon-code"
        style={{ border: `1px dashed ${colors[1]}`, color: colors[1] }}
      >
        <span className="code">{id}</span>
      </div>
      <div className="coupon-line" />
      <div className="coupon-footer">
        <div>
          <p className="text-xs">{t("planManager.coupon.expiryDate")}</p>
          <p className="text-sm">
            {redeem_by
              ? moment(redeem_by * 1000).format("MMM D, YYYY @ HH:mm")
              : "-"}
          </p>
        </div>
        <div>
          <p className="text-xs">{t("planManager.coupon.maximumRedemption")}</p>
          <p className="text-sm">
            {max_redemptions
              ? `${max_redemptions} ${max_redemptions === 1 ? t("planManager.coupon.singularTime") : t("planManager.coupon.times")}`
              : "-"}{" "}
            ({t("planManager.coupon.used")} {times_redeemed})
          </p>
        </div>
      </div>
      <button className="delete-button" onClick={deleteCoupon}>
        <SvgIcon src="/img/new-icons/red-trash.svg" />
      </button>
      {valid && (
        <button className="copy-button" onClick={copyCode}>
          {t("planManager.coupon.copy")}
        </button>
      )}
      <div className={showTooltip ? "shown" : ""} role="tooltip">
        {t("planManager.coupon.copied")}
      </div>
    </article>
  );
};

Coupon.propTypes = {
  coupon: PropTypes.shape({
    id: PropTypes.string.isRequired,
    amount_off: PropTypes.number,
    currency: PropTypes.string,
    duration: PropTypes.string.isRequired,
    duration_in_months: PropTypes.number,
    percent_off: PropTypes.number,
    name: PropTypes.string,
    redeem_by: PropTypes.number,
    max_redemptions: PropTypes.number,
    metadata: PropTypes.object,
    times_redeemed: PropTypes.number,
    valid: PropTypes.bool,
  }).isRequired,
};

export default Coupon;
